import { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence, LayoutGroup } from "framer-motion"
import Reveal, { SectionLabel } from "./Reveal"
import { projects } from "./data"

const filters = ["Todos", "Sites", "Sistemas", "E-commerce"]

const Projetos = () => {
  const [filter, setFilter] = useState("Todos")
  const [open, setOpen] = useState(null)
  const [isMobile, setIsMobile] = useState(false)
  const [slide, setSlide] = useState(0)
  const trackRef = useRef(null)
  const list = filter === "Todos" ? projects : projects.filter((p) => p.category === filter)

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)")
    const update = () => setIsMobile(mq.matches)
    update()
    mq.addEventListener?.("change", update)
    return () => mq.removeEventListener?.("change", update)
  }, [])

  useEffect(() => {
    setSlide(0)
    if (trackRef.current) trackRef.current.scrollTo({ left: 0 })
  }, [filter])

  const onTrackScroll = () => {
    const el = trackRef.current
    if (!el || !isMobile) return
    const card = el.firstElementChild
    if (!card) return
    const step = card.getBoundingClientRect().width + 14
    setSlide(Math.min(list.length - 1, Math.max(0, Math.round(el.scrollLeft / step))))
  }

  const goTo = (i) => {
    const el = trackRef.current
    const target = el?.children[i]
    if (!target) return
    el.scrollTo({ left: target.offsetLeft - el.offsetLeft - el.clientLeft, behavior: "smooth" })
  }

  useEffect(() => {
    if (!open) return
    const onKey = (e) => e.key === "Escape" && setOpen(null)
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open])

  return (
    <section className="k-section k-projects" id="projetos">
      <div className="k-container">
        <div className="k-projects__head">
          <Reveal>
            <SectionLabel index="03">Portfólio</SectionLabel>
            <h2 className="k-h2">
              Projetos que já <em>saíram do papel</em>.
            </h2>
          </Reveal>

          <Reveal delay={0.1} className="k-filters" role="tablist" aria-label="Filtrar projetos">
            <LayoutGroup id="filters">
              {filters.map((f) => (
                <button
                  key={f}
                  role="tab"
                  aria-selected={filter === f}
                  className={`k-filter ${filter === f ? "is-active" : ""}`}
                  onClick={() => setFilter(f)}
                >
                  {filter === f && <motion.span layoutId="filter-pill" className="k-filter__pill" />}
                  <span className="k-filter__label">{f}</span>
                </button>
              ))}
            </LayoutGroup>
          </Reveal>
        </div>

        <motion.div layout={!isMobile} className="k-projects__grid" ref={trackRef} onScroll={onTrackScroll}>
          <AnimatePresence mode="popLayout">
            {list.map((p, i) => (
              <motion.article
                layout={!isMobile}
                key={p.name}
                className={`k-project ${filter === "Todos" && i < 2 ? "is-large" : ""}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <button className="k-project__btn" onClick={() => setOpen(p)} aria-label={`Ampliar ${p.name}`}>
                  <div className="k-project__frame">
                    <div className="k-project__bar">
                      <i />
                      <i />
                      <i />
                      <span>{p.name}</span>
                    </div>
                    <div className="k-project__img">
                      <img src={p.image} alt={`Tela do projeto ${p.name}`} loading="lazy" />
                    </div>
                  </div>
                  <div className="k-project__info">
                    <div>
                      <h3>{p.name}</h3>
                      <p>{p.details}</p>
                    </div>
                    <span className="k-project__cat">{p.category}</span>
                  </div>
                </button>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        {isMobile && list.length > 1 && (
          <div className="k-slider-nav">
            <span className="k-slider-nav__count">
              {String(slide + 1).padStart(2, "0")} <i>/ {String(list.length).padStart(2, "0")}</i>
            </span>
            <span className="k-slider-nav__bar">
              <span style={{ width: `${((slide + 1) / list.length) * 100}%` }} />
            </span>
            <button onClick={() => goTo(Math.max(0, slide - 1))} disabled={slide === 0} aria-label="Projeto anterior">
              ←
            </button>
            <button
              onClick={() => goTo(Math.min(list.length - 1, slide + 1))}
              disabled={slide === list.length - 1}
              aria-label="Próximo projeto"
            >
              →
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="k-lightbox"
            onClick={() => setOpen(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.figure
              initial={{ scale: 0.92, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={open.image} alt={`Tela do projeto ${open.name}`} />
              <figcaption>
                <strong>{open.name}</strong> — {open.details}
              </figcaption>
              <button className="k-lightbox__close" onClick={() => setOpen(null)} aria-label="Fechar">
                ✕
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}

export default Projetos
